import { apiClient } from '@/api/client';
import type {
  MarkAllReadResponse,
  MarkReadResponse,
  NotificationListQuery,
  NotificationListResponse,
  UnreadCountResponse,
} from '@/types/shared';

/**
 * T111/T114 own-scoped notifications REST contract (`packages/shared/src/contracts/notifications.ts`).
 * `recipientUserId` is never a param — every path scopes to the authenticated caller server-side.
 */

/**
 * `GET /notifications` — newest-first, paginated. `unreadOnly: true` is the reconnect-backfill call
 * (notifications-design.md §4 "backfill flow") issued on every socket (re)connect; the plain
 * paginated call (no `unreadOnly`) seeds the panel's initial mixed list and powers "load more".
 */
export async function listNotifications(
  query?: NotificationListQuery,
): Promise<NotificationListResponse> {
  const { data } = await apiClient.get<NotificationListResponse>('/notifications', {
    params: query,
  });
  return data;
}

/** `GET /notifications/unread-count` — lightweight badge re-sync, not used by the main data flow
 * (the list/mark responses already carry `unreadCount`) but exposed for completeness/future use. */
export async function getUnreadCount(): Promise<UnreadCountResponse> {
  const { data } = await apiClient.get<UnreadCountResponse>('/notifications/unread-count');
  return data;
}

/** `PATCH /notifications/:id/read` — idempotent; returns the updated item + fresh `unreadCount`. */
export async function markNotificationRead(id: string): Promise<MarkReadResponse> {
  const { data } = await apiClient.patch<MarkReadResponse>(`/notifications/${id}/read`);
  return data;
}

/** `POST /notifications/read-all` — returns how many rows transitioned + `unreadCount: 0`. */
export async function markAllNotificationsRead(): Promise<MarkAllReadResponse> {
  const { data } = await apiClient.post<MarkAllReadResponse>('/notifications/read-all');
  return data;
}
